<script>
export default {
  name: 'ColorInputField',
  components: {
    ValidationMessage: () => import('@/components/general/ValidationMessage')

  },

  props: {
    value: {
      type: String,
      default: ''
    },

    label: {
      type: String,
      default: null
    },

    placeholder: {
      type: String,
      default: ''
    },

    disabled: {
      type: Boolean,
      default: false
    },

    readonly: {
      type: Boolean,
      default: false
    },

    validation: {
      type: Object,
      default: null
    }
  },

  computed: {
    isValidHex () {
      return /^#([0-9A-Fa-f]{6})$/.test(this.value)
    },

    isRequired () {
      if (!this.validation) {
        return false
      }

      if (this.validation.$params && this.validation.$params.required) {
        const requiredParam = this.validation.$params.required

        if (requiredParam.type === 'requiredIf' && requiredParam.prop && typeof requiredParam.prop === 'function') {
          const result = requiredParam.prop()

          return result
        }

        return true
      }

      return false
    }
  },

  methods: {
    onInput (val) {
      if (val === '' || val === null) {
        this.$emit('input', '')

        return
      }

      if (!val.startsWith('#')) {
        val = '#' + val.replace(/[^0-9A-Fa-f]/g, '')
      }

      if (/^#([0-9A-Fa-f]{0,6})$/.test(val)) {
        this.$emit('input', val)
      }
    },

    onColorChange (val) {
      this.$emit('input', val)
    },

    onBlur () {
      if (this.isRequired && this.value && !/^#([0-9A-Fa-f]{6})$/.test(this.value)) {
        this.$emit('input', '#000000')
      }
    },

    isDark (hex) {
      if (!/^#([0-9A-Fa-f]{6})$/.test(hex)) return false
      const r = parseInt(hex.substr(1, 2), 16)
      const g = parseInt(hex.substr(3, 2), 16)
      const b = parseInt(hex.substr(5, 2), 16)

      return 0.299 * r + 0.587 * g + 0.114 * b < 150
    },

    clearValue () {
      this.$emit('input', '')
    }
  }
}
</script>

<template>
  <div class="form-item color-input-field">
    <label
      v-if="label"
      class="color-input-field__label"
      :for="'color-input' + _uid"
    >
      {{ label }}

      <span v-if="!isRequired && !disabled">{{ $t('global:form.optional') }}</span>
    </label>

    <div class="color-input-field__wrapper">
      <input
        type="color"
        class="color-picker"
        :value="isValidHex ? value : '#ffffff'"
        :disabled="disabled"
        :style="{ backgroundColor: isValidHex ? value : '#fff' }"
        @input="onColorChange($event.target.value)"
      >

      <input
        :id="'color-input' + _uid"
        class="form-input color-input-field__text-input"
        type="text"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :value="value"
        maxlength="7"
        :style="{ backgroundColor: isValidHex ? value : '#ffffff', color: isDark(value) ? '#ffffff' : '#222222' }"
        @input="onInput($event.target.value)"
        @blur="onBlur"
      >

      <button
        v-if="!isRequired"
        class="color-input-field__button"
        :disabled="disabled || readonly"
        type="button"
        @click="clearValue"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20px"
          height="20px"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M5.29289 5.29289C5.68342 4.90237 6.31658 4.90237 6.70711 5.29289L12 10.5858L17.2929 5.29289C17.6834 4.90237 18.3166 4.90237 18.7071 5.29289C19.0976 5.68342 19.0976 6.31658 18.7071 6.70711L13.4142 12L18.7071 17.2929C19.0976 17.6834 19.0976 18.3166 18.7071 18.7071C18.3166 19.0976 17.6834 19.0976 17.2929 18.7071L12 13.4142L6.70711 18.7071C6.31658 19.0976 5.68342 19.0976 5.29289 18.7071C4.90237 18.3166 4.90237 17.6834 5.29289 17.2929L10.5858 12L5.29289 6.70711C4.90237 6.31658 4.90237 5.68342 5.29289 5.29289Z"
            fill="#00000080"
          />
        </svg>
      </button>
    </div>

    <div class="form-input-details">
      <ValidationMessage
        v-if="validation"
        :validation="validation"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.color-input-field {
  &__label {
    color: rgba(0, 0, 0, 0.7);
    font-size: 1.6em;
    font-weight: 100;
    line-height: 1em;
    transition: all 200ms;
    cursor: text;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 100%;
    margin-bottom: 12px;
    display: block;
  }

  &__wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__text-input {
    transition: background 0.2s;
    width: 100px;
    text-align: center;
    height: 40px;
    padding: 0 12px;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    box-shadow: rgba(0, 0, 0, 0.09) 0px 0px 0px 1px;
  }

  &__button {
    cursor: pointer;
    padding: 15px;
  }

  input[type="color"] {
    appearance: none;
    -webkit-appearance: none;
    border: none;
    width: 18px;
    height: 18px;
    border-radius: 100%;
    border: 1px solid #ccc;
    border-radius: 50%;
    border: none;
    box-shadow: rgba(0, 0, 0, 0.09) 0px 0px 0px 1px;
    padding: 0;
    cursor: pointer;
  }

  input[type="color"]::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  input[type="color"]::-webkit-color-swatch {
    border-radius: 50%;
    border: none;
  }
}
</style>
